import { Icon } from '@blueprintjs/core';
import UserSelect from 'Components/Generic/BOSFilterSelect/UserSelect';
import { showSaveSuccessToast } from 'Components/Generic/BOSToasterUtil';
import ButtonFooter from 'Components/Generic/Buttons/ButtonFooter';
import SaveButton from 'Components/Generic/Buttons/SaveButton';
import UndoButton from 'Components/Generic/Buttons/UndoButton';
import DetailTable from 'Components/Generic/DetailPanel/DetailTable';
import EditableDetailPanel from 'Components/Generic/DetailPanel/EditableDetailPanel';
import { user_roles } from 'Data/Enum';
import OrgData from 'Data/OrgData';
import DataManager from 'Lib/DataManager';
import { useUndoableSaveAction } from 'Lib/hooks/useUndoableSaveAction';
import { useUpdatableObject } from 'Lib/hooks/useUpdatableObject';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { isUserAuthorized } from 'Util';
import { getTagValue, setTagValue } from 'Util/tagUtil';

let DM = new DataManager();

const OrganizationInformationPanel = ({
  orgId,
  orgObject,
  title = 'Core Facility',
  editable = false,
  collapsible = true,
  ...props
}) => {
  const {
    currentObject: org,
    hasBeenUpdated,
    reset: resetOrg,
    updateField,
    originalObject: originalOrg,
    doSave,
  } = useUpdatableObject(OrgData.updateRecord, OrgData.createOrg);
  const [resetUndo, showSaveUndoToast] = useUndoableSaveAction({
    originalObject: originalOrg,
    undoFunction: OrgData.updateRecord,
    onUndo: (originalOrg) => fetchOrg(originalOrg.id),
    objectType: 'Core Facility',
  });

  useEffect(() => {
    DM.add(
      OrgData.registerListener('BLANK_RECORD_LOADED', onOrgLoaded),
      OrgData,
    );
    DM.add(OrgData.registerListener('RECORD_LOADED', onOrgLoaded), OrgData);
    DM.add(OrgData.registerListener('RECORD_CREATED', onOrgCreated), OrgData);
    DM.add(OrgData.registerListener('RECORD_UPDATED', onOrgUpdated), OrgData);
    return () => {
      DM.clear();
    };
  }, []);

  useEffect(() => {
    if (orgId && orgId >= 0) {
      fetchOrg(orgId);
    }
  }, [orgId]);

  useEffect(() => {
    if (!editable && orgObject) {
      resetOrg(orgObject);
    } else if (orgObject?.id && Number(orgObject.id) >= 0) {
      fetchOrg(orgObject.id);
    }
  }, [orgObject]);

  const onOrgLoaded = (payload) => {
    if (payload) {
      resetOrg(payload);
      resetUndo(() => fetchOrg(payload.id));
    }
  };

  const fetchOrg = (id) => {
    OrgData.getOrgAndAssociations(id);
  };

  const onOrgCreated = (payload) => {
    if (payload) {
      showSaveSuccessToast('Core Facility Created');
      fetchOrg(payload.id);
    }
  };

  const onOrgUpdated = (payload) => {
    if (payload) {
      showSaveUndoToast();
    }
  };

  let getDetailData = (inEditState) => {
    return [
      {
        label: 'Name',
        value: org.name,
        editableFields: ['value'],
        onEdit: (newValue) => {
          updateField('name', newValue);
        },
        readOnly: !isUserAuthorized({
          roles: [user_roles.ADMINISTRATOR],
        }),
        isRequired: true,
      },
      {
        label: 'Short Name',
        value: org.short_name,
        editableFields: ['value'],
        onEdit: (newValue) => {
          updateField('short_name', newValue);
        },
        readOnly: !isUserAuthorized({
          roles: [user_roles.ADMINISTRATOR],
        }),
        isRequired: true,
      },
      {
        label: 'PI',
        value: inEditState ? (
          <div>
            <UserSelect
              readOnly={
                !isUserAuthorized({ roles: [user_roles.ADMINISTRATOR] })
              }
              defaultSelectedId={org.pi_id}
              onSelectUser={(user) => updateField('pi_id', user?.id || null)}
            />
          </div>
        ) : (
          <span>{org.pi?.full_name}</span>
        ),
      },
      // {
      //   label: 'Status',
      //   value: org.status,
      // },
      {
        label: 'Delivery Code',
        value: org.delivery_code,
        onEdit: (newValue) => {
          updateField('delivery_code', newValue);
        },
        editableFields: ['value'],
        readOnly: !isUserAuthorized({
          roles: [user_roles.ADMINISTRATOR],
        }),
      },
      {
        label: 'Department',
        value: org.department,
        onEdit: (newValue) => {
          updateField('department', newValue);
        },
        editableFields: ['value'],
        readOnly: !isUserAuthorized({
          roles: [user_roles.ADMINISTRATOR],
        }),
      },
      {
        label: 'Division',
        value: org.division,
        onEdit: (newValue) => {
          updateField('division', newValue);
        },
        editableFields: ['value'],
        readOnly: !isUserAuthorized({
          roles: [user_roles.ADMINISTRATOR],
        }),
      },

      {
        label: 'Track Resources',
        value: (
          <div style={{ display: 'flex' }}>
            {inEditState && (
              <input
                type='checkbox'
                checked={!!getTagValue(org, 'line_item_resources')}
                onChange={(event) => {
                  updateField(
                    'tags',
                    setTagValue(
                      org,
                      'line_item_resources',
                      event.target.checked,
                    ),
                  );
                }}
              />
            )}
            {!inEditState && (
              <Icon
                size='20'
                icon={
                  getTagValue(org, 'line_item_resources') ? 'tick' : 'blank'
                }
              />
            )}
          </div>
        ),
      },

      ...(isUserAuthorized({
        roles: [user_roles.ADMINISTRATOR],
      })
        ? [
            {
              label: 'Org ID',
              value: getTagValue(org, 'org_id'),
              editableFields: ['value'],
              onEdit: (newValue) => {
                updateField('tags', setTagValue(org, 'org_id', newValue));
              },
            },
          ]
        : []),

      ...(isUserAuthorized({
        roles: [user_roles.ADMINISTRATOR, user_roles.TRANS_IMPORTER],
      })
        ? [
            {
              label: 'Resource Group Id',
              value: getTagValue(org, 'resource_group_id'),
              editableFields: ['value'],
              onEdit: (newValue) => {
                updateField(
                  'tags',
                  setTagValue(org, 'resource_group_id', newValue),
                );
              },
            },
          ]
        : []),
      ...(isUserAuthorized({
        roles: [user_roles.ADMINISTRATOR, user_roles.TRANS_IMPORTER],
      })
        ? [
            {
              label: 'Revenue Expense Code',
              value: getTagValue(org, 'revenue_expense_code'),
              editableFields: ['value'],
              onEdit: (newValue) => {
                updateField(
                  'tags',
                  setTagValue(org, 'revenue_expense_code', newValue),
                );
              },
            },
          ]
        : []),
    ];
  };

  const undoButton = editable && hasBeenUpdated && (
    <UndoButton clickHandler={() => fetchOrg(org.id)} />
  );

  const saveButton = editable && hasBeenUpdated && (
    <SaveButton clickHandler={doSave} />
  );

  const footer = (
    <ButtonFooter>
      {undoButton}
      {saveButton}
    </ButtonFooter>
  );

  return (
    <EditableDetailPanel
      title={title}
      footer={footer}
      editable={editable}
      collapsible={collapsible}
      {...props}
      renderChild={(inEditState) => {
        return (
          <DetailTable
            data={getDetailData(inEditState)}
            editable={inEditState}
          />
        );
      }}
    />
  );
};

OrganizationInformationPanel.propTypes = {
  orgId: PropTypes.number,
  orgObject: PropTypes.object,
  title: PropTypes.string,
  editable: PropTypes.bool,
  collapsible: PropTypes.bool,
};

export default OrganizationInformationPanel;
